import { useEffect, useState } from 'react'
import { FormProvider, useForm, type FieldErrors, type Resolver } from 'react-hook-form'
import { Box, useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { Textarea } from '@components/shared/Textarea'
import { unMaskCurrency } from '@utils/masks/unMaskCurrency'
import { useDepreciation } from '@pages/Depreciation/context'
import { useUser } from '@hooks/useUser'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { editDetailedSummaryDepreciationModalSchema } from './form/validations'
import { setInitialValues } from './form/setInitialValues'

import type { AddRditOtherConsumptionsModalProps } from './types'
import type { EditDetailedSummaryDepreciationFormProps } from './form/types'

import * as S from './styles'

export const EditDetailedSummaryDepreciation = ({
  data,
  open,
  onClose,
  canEdit = true,
  ...rest
}: AddRditOtherConsumptionsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData } = useUser()

  const {
    detailedSummaryDepreciationData,
    isDetailedSummaryDepreciationLoading,
    isEditDetailedSummaryDepreciationLoading,
    editDetailedSummaryDepreciation
  } = useDepreciation()

  const methods = useForm<EditDetailedSummaryDepreciationFormProps>({
    defaultValues: initialFormValues,
    resolver:
      editDetailedSummaryDepreciationModalSchema as unknown as Resolver<EditDetailedSummaryDepreciationFormProps>
  })

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)

  const accountDescriptionOptions = detailedSummaryDepreciationData?.availableFilters?.expenseTypes
    ?.map((option) => ({
      label: option.description,
      value: option.accountingCode
    }))
    .filter((option) => option.value !== 'Todos')

  const onError = (errors: FieldErrors<EditDetailedSummaryDepreciationFormProps>) => console.log({ errors })

  const onSubmit = (formData: EditDetailedSummaryDepreciationFormProps) => {
    if (!canEdit) return

    formData.filiCode = filiData?.filiCode!
    formData.competenceDate = filiData?.startPeriod!

    editDetailedSummaryDepreciation(formData).then((success) => success && onClose(false))
  }

  useEffect(() => {
    !!data && !!open && methods.reset(setInitialValues(data!))
  }, [data, open])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        title="Editar Depreciação"
        headerIcon={<EditIcon />}
        onClose={() => setOpenCancelDetailedSummaryModal(true)}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: canEdit ? 'Salvar' : undefined,
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: canEdit ? () => methods.handleSubmit(onSubmit, onError)() : undefined,
          loading: isEditDetailedSummaryDepreciationLoading
        }}
        {...rest}
      >
        <S.Title>Preencha todos os campos antes de confirmar a edição da depreciação.</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={4}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <Select
                  required
                  name="accountDescription"
                  label="Descrição Conta"
                  placeholder="Selecione a desc. da conta"
                  options={!!accountDescriptionOptions?.length ? accountDescriptionOptions : []}
                  onChange={({ target }) => {
                    const accountDescriptionText = accountDescriptionOptions?.find(
                      (option) => option.value === target.value
                    )?.label

                    methods.setValue('accountDescription', accountDescriptionText as string)
                    methods.setValue('accountCode', target.value as string)
                  }}
                  loading={isDetailedSummaryDepreciationLoading}
                  disabled
                />
              </Box>
              <Input name="accountCode" label="Conta" placeholder="Nº da Conta" required disabled />
              <Input
                required
                name="depreciationValue"
                label="Valor Depreciação"
                placeholder="R$ 0,00"
                maskType="currency"
                onBlur={({ target }) => {
                  methods.setValue('depreciationValue', unMaskCurrency(target.value).toFixed(2))
                }}
                disabled={!canEdit || isDetailedSummaryDepreciationLoading}
              />
            </RowBox>

            <RowBox>
              <Textarea
                required
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações"
                disabled={!canEdit}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />
    </>
  )
}
